import {
  cleanStepDisplayText,
  looksLikeSpecificationLine,
  stripInlineMarkdown,
} from "./ragChatUtils";

/** One labeled value inside a specification group (e.g. Front → 35 PSI). */
export interface SpecSheetValue {
  key: string | null;
  value: string;
}

/** A named group of related specification values (e.g. Standard Load). */
export interface SpecSheetGroup {
  name: string;
  /** Qualifier from the title, e.g. "if equipped" from "Spare (if equipped)". */
  qualifier: string | null;
  values: SpecSheetValue[];
}

/** Pivot table built from groups that share the same child keys. */
export interface SpecSheetTable {
  /** First-column header (row dimension). */
  rowHeader: string;
  columnHeaders: string[];
  rows: Array<{ label: string; cells: string[] }>;
}

/** Singleton callout (e.g. spare tire pressure). */
export interface SpecSheetCallout {
  label: string;
  value: string;
  qualifier: string | null;
}

export interface ParsedSpecSheet {
  /** Leading title / metadata prose above the first spec section. */
  intro: string | null;
  /** Heading for the primary specification block. */
  sectionTitle: string | null;
  table: SpecSheetTable | null;
  callouts: SpecSheetCallout[];
  /** Groups that could not be pivoted into the main table. */
  leftoverGroups: SpecSheetGroup[];
  notesTitle: string | null;
  notes: string[];
  /** Trailing prose that is not a duplicate of displayed values. */
  trailingProse: string | null;
}

const BULLET_RE = /^[-*•]\s+(.*)$/;
const HEADING_RE = /^(?:\*\*([^*]+)\*\*|__([^_]+)__|#{1,4}\s+(.+?))\s*:?\s*$/;
const KEY_VALUE_RE = /^(.{1,48}?):\s+(.+)$/;
const NOTES_HEADING_RE = /^(?:notes?|important(?:\s+notes?)?|caveats?|remarks?)\b/i;

function cleanLine(line: string): string {
  return stripInlineMarkdown(cleanStepDisplayText(line)).trim();
}

function isHeading(line: string): boolean {
  const t = line.trim();
  return HEADING_RE.test(t) || (/^\*\*.+\*\*$/.test(t) && t.length <= 100);
}

function headingText(line: string): string {
  const t = line.trim();
  const m = t.match(HEADING_RE);
  if (m) return (m[1] ?? m[2] ?? m[3] ?? "").replace(/:$/, "").trim();
  return cleanLine(t).replace(/:$/, "").trim();
}

function isNotesHeading(text: string): boolean {
  return NOTES_HEADING_RE.test(cleanLine(text).replace(/:$/, ""));
}

function parseKeyValue(text: string): SpecSheetValue | null {
  const clean = cleanLine(text);
  if (!clean) return null;
  const m = clean.match(KEY_VALUE_RE);
  if (m) {
    const key = m[1]!.trim();
    const value = m[2]!.trim();
    if (!key || !value) return null;
    // Long narrative after a colon is a note, not a cell value.
    if (value.length > 80 && !looksLikeSpecificationLine(value)) return null;
    if (key.split(/\s+/).length > 6) return null;
    return { key, value };
  }
  if (looksLikeSpecificationLine(clean) || /^(?:\d+(?:\.\d+)?)\s*[A-Za-z%°]+/.test(clean)) {
    return { key: null, value: clean };
  }
  return null;
}

function splitNameQualifier(name: string): { name: string; qualifier: string | null } {
  const m = name.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  if (!m) return { name: name.trim(), qualifier: null };
  return { name: m[1]!.trim(), qualifier: m[2]!.trim() };
}

/** Short title-like bullet used as a group label (not a key:value or long note). */
function isGroupHeaderBullet(text: string): boolean {
  const clean = cleanLine(text);
  if (!clean || clean.length > 60) return false;
  if (parseKeyValue(clean)) return false;
  if (/[.!?]$/.test(clean) && clean.length > 40) return false;
  if (clean.split(/\s+/).length > 8) return false;
  // Avoid treating note-like lead-ins as groups.
  if (/^(?:always|never|do\s+not|pressures?\s+are|based\s+on)\b/i.test(clean)) {
    return false;
  }
  return true;
}

function isCompactMeasurementValue(value: string): boolean {
  const v = value.trim();
  if (!v || v.length > 40) return false;
  if (looksLikeSpecificationLine(v)) return true;
  return /^(?:\d+(?:\.\d+)?)\s*[A-Za-z%°/]+/.test(v);
}

function bareKeyLabel(key: string): string {
  return key.replace(/\s*\([^)]*\)\s*/g, " ").trim() || key;
}

/** Lowercase key signature for a multi-key group, or null if not pivot-ready. */
function groupKeySignature(group: SpecSheetGroup): string | null {
  const keys = group.values
    .map((v) => v.key)
    .filter((k): k is string => Boolean(k && k.trim()));
  if (keys.length < 2 || keys.length !== group.values.length) return null;
  return keys.map((k) => k.toLowerCase()).join("|");
}

/**
 * Signature shared by at least two groups in `probe` (order-sensitive, as stored).
 */
function sharedMultiKeySignature(probe: SpecSheetGroup[]): string | null {
  const counts = new Map<string, number>();
  for (const g of probe) {
    const sig = groupKeySignature(g);
    if (!sig) continue;
    counts.set(sig, (counts.get(sig) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  Array.from(counts.entries()).forEach(([sig, count]) => {
    if (count > bestCount) {
      best = sig;
      bestCount = count;
    }
  });
  return bestCount >= 2 ? best : null;
}

/**
 * True when a keyed measurement should close the open group and become a
 * singleton callout (e.g. after Standard/Full Load share Front|Rear).
 *
 * Must NOT fire for a third in-group column (Nominal) or for qualified column
 * keys that belong to the shared table signature (Front (cold)).
 */
function isStandaloneCalloutKeyValue(
  kv: SpecSheetValue,
  current: SpecSheetGroup | null,
  completedGroups: SpecSheetGroup[]
): boolean {
  if (!kv.key || !kv.value || !isCompactMeasurementValue(kv.value)) return false;

  const bare = bareKeyLabel(kv.key);
  if (!isGroupHeaderBullet(bare)) return false;

  // Include the in-progress group so Spare after Full Load still sees the
  // shared Front|Rear structure before pushCurrent().
  const probe: SpecSheetGroup[] = [...completedGroups];
  if (current && current.values.length > 0) {
    probe.push(current);
  }

  const sharedSig = sharedMultiKeySignature(probe);
  if (!sharedSig) return false;

  const sigKeys = sharedSig.split("|");
  const keyNorm = kv.key.toLowerCase();
  // Column keys already in the shared table (including qualified forms) stay cells.
  if (sigKeys.includes(keyNorm)) return false;

  return true;
}

function calloutGroupFromKeyValue(kv: SpecSheetValue): SpecSheetGroup {
  const { name, qualifier } = splitNameQualifier(String(kv.key));
  return {
    name,
    qualifier,
    values: [{ key: null, value: kv.value.trim() }],
  };
}

function collectDisplayedValues(sheet: {
  table: SpecSheetTable | null;
  callouts: SpecSheetCallout[];
  leftoverGroups: SpecSheetGroup[];
}): string[] {
  const values: string[] = [];
  if (sheet.table) {
    for (const row of sheet.table.rows) {
      for (const cell of row.cells) {
        if (cell.trim()) values.push(cell.trim());
      }
    }
  }
  for (const c of sheet.callouts) {
    if (c.value.trim()) values.push(c.value.trim());
  }
  for (const g of sheet.leftoverGroups) {
    for (const v of g.values) {
      if (v.value.trim()) values.push(v.value.trim());
    }
  }
  return values;
}

/**
 * True when trailing prose only restates values already shown in the sheet
 * (and does not add a material new caveat).
 */
export function isDuplicateSpecSummary(
  prose: string,
  displayedValues: string[]
): boolean {
  const clean = cleanLine(prose);
  if (!clean || clean.length < 20) return false;
  if (!displayedValues.length) return false;

  const normalizedValues = displayedValues.map((v) =>
    v.toLowerCase().replace(/\s+/g, " ").trim()
  );
  const proseLower = clean.toLowerCase();
  const matched = normalizedValues.filter((v) => proseLower.includes(v));
  if (matched.length < Math.min(2, normalizedValues.length)) return false;
  if (matched.length < Math.ceil(normalizedValues.length * 0.6)) return false;

  // Keep paragraphs that add instructions / warnings beyond value restatement.
  if (
    /\b(?:always|never|do\s+not|warning|caution|important|verify|must|should\s+not)\b/i.test(
      clean
    ) &&
    matched.length < normalizedValues.length
  ) {
    return false;
  }
  return true;
}

function pivotGroupsToTable(
  groups: SpecSheetGroup[],
  rowHeader: string
): { table: SpecSheetTable; used: Set<number> } | null {
  const candidates: Array<{ index: number; keys: string[] }> = [];
  for (let i = 0; i < groups.length; i++) {
    const g = groups[i]!;
    const keys = g.values
      .map((v) => v.key)
      .filter((k): k is string => Boolean(k && k.trim()));
    if (keys.length >= 2 && keys.length === g.values.length) {
      candidates.push({ index: i, keys });
    }
  }
  if (candidates.length < 2) return null;

  // Prefer the most common key signature among candidate groups.
  const sigCount = new Map<string, { keys: string[]; indices: number[] }>();
  for (const c of candidates) {
    const sig = c.keys.map((k) => k.toLowerCase()).join("|");
    const prev = sigCount.get(sig);
    if (prev) prev.indices.push(c.index);
    else sigCount.set(sig, { keys: c.keys, indices: [c.index] });
  }
  type SigEntry = { keys: string[]; indices: number[] };
  const best = Array.from(sigCount.values()).reduce<SigEntry | null>(
    (acc, entry) =>
      !acc || entry.indices.length > acc.indices.length ? entry : acc,
    null
  );
  if (!best || best.indices.length < 2) return null;

  const used = new Set(best.indices);
  const rows = best.indices.map((idx: number) => {
    const g = groups[idx]!;
    const byKey = new Map(
      g.values.map((v) => [String(v.key).toLowerCase(), v.value] as const)
    );
    return {
      label: g.name,
      cells: best.keys.map((k) => byKey.get(k.toLowerCase()) ?? ""),
    };
  });

  return {
    table: {
      rowHeader,
      columnHeaders: best.keys,
      rows,
    },
    used,
  };
}

function groupsToCallouts(groups: SpecSheetGroup[]): {
  callouts: SpecSheetCallout[];
  leftover: SpecSheetGroup[];
} {
  const callouts: SpecSheetCallout[] = [];
  const leftover: SpecSheetGroup[] = [];
  for (const g of groups) {
    if (g.values.length === 1 && g.values[0]!.key == null) {
      callouts.push({
        label: g.name,
        value: g.values[0]!.value,
        qualifier: g.qualifier,
      });
      continue;
    }
    if (g.values.length === 1 && g.values[0]!.key) {
      // Single keyed value → compact callout "Name — Key: Value" as label/value.
      callouts.push({
        label: g.name,
        value: `${g.values[0]!.key}: ${g.values[0]!.value}`,
        qualifier: g.qualifier,
      });
      continue;
    }
    leftover.push(g);
  }
  return { callouts, leftover };
}

/**
 * Parse a specification answer with nested (or flat sibling) bullet groups into
 * a compact sheet model. Returns null when the answer is not a grouped spec.
 *
 * @param rowHeaderLabel First-column header for pivoted tables (i18n from UI).
 */
export function parseGroupedSpecificationAnswer(
  text: string,
  rowHeaderLabel = "Condition"
): ParsedSpecSheet | null {
  const raw = String(text ?? "").replace(/\r\n/g, "\n").trim();
  if (!raw) return null;

  const lines = raw.split("\n");
  /** Headings / prose before the first specification group. */
  const preamble: Array<{ kind: "heading" | "prose"; text: string }> = [];
  let notesTitle: string | null = null;
  const notes: string[] = [];
  const trailingLines: string[] = [];
  const groups: SpecSheetGroup[] = [];
  let current: SpecSheetGroup | null = null;
  let inNotes = false;
  let seenSpecContent = false;

  const pushCurrent = () => {
    // Empty groups (e.g. a parent "Position" wrapper with no direct values)
    // are discarded so they never block pivoting.
    if (current && current.values.length > 0) {
      groups.push(current);
    }
    current = null;
  };

  const pushStandaloneCallout = (kv: SpecSheetValue) => {
    pushCurrent();
    groups.push(calloutGroupFromKeyValue(kv));
    seenSpecContent = true;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    if (isHeading(trimmed)) {
      const title = headingText(trimmed);
      if (!title) continue;
      if (isNotesHeading(title)) {
        pushCurrent();
        inNotes = true;
        notesTitle = title;
        seenSpecContent = true;
        continue;
      }
      if (inNotes) {
        inNotes = false;
        trailingLines.push(trimmed);
        continue;
      }
      if (!seenSpecContent) {
        preamble.push({ kind: "heading", text: title });
        continue;
      }
      trailingLines.push(trimmed);
      continue;
    }

    const bullet = trimmed.match(BULLET_RE);
    if (bullet) {
      const body = (bullet[1] ?? "").trim();
      const cleanedBody = cleanLine(body);
      if (inNotes) {
        notes.push(cleanedBody);
        continue;
      }

      // Bullet "Notes" / "Important" — same as a Markdown notes heading.
      if (isNotesHeading(cleanedBody)) {
        pushCurrent();
        inNotes = true;
        notesTitle = cleanedBody.replace(/:$/, "").trim() || "Notes";
        seenSpecContent = true;
        continue;
      }

      if (isGroupHeaderBullet(body)) {
        pushCurrent();
        const { name, qualifier } = splitNameQualifier(cleanedBody);
        current = { name, qualifier, values: [] };
        seenSpecContent = true;
        continue;
      }

      const kv = parseKeyValue(body);
      if (kv && isStandaloneCalloutKeyValue(kv, current, groups)) {
        pushStandaloneCallout(kv);
        continue;
      }
      if (kv && current) {
        current.values.push(kv);
        seenSpecContent = true;
        continue;
      }
      if (kv && !current) {
        const fallbackName =
          [...preamble].reverse().find((p) => p.kind === "heading")?.text ||
          "Specification";
        current = { name: fallbackName, qualifier: null, values: [kv] };
        seenSpecContent = true;
        continue;
      }

      // Unrecognized bullet after groups → treat as notes.
      if (seenSpecContent && (groups.length > 0 || current)) {
        pushCurrent();
        if (!notesTitle) notesTitle = "Notes";
        notes.push(cleanedBody);
        inNotes = true;
        continue;
      }

      if (!seenSpecContent) {
        preamble.push({ kind: "prose", text: trimmed });
      }
      continue;
    }

    if (inNotes) {
      notes.push(cleanLine(trimmed));
      continue;
    }
    if (!seenSpecContent) {
      preamble.push({ kind: "prose", text: trimmed });
      continue;
    }
    trailingLines.push(trimmed);
  }
  pushCurrent();

  if (groups.length < 1) return null;

  const keyedGroups = groups.filter(
    (g) => g.values.filter((v) => v.key).length >= 2
  );
  const singletonGroups = groups.filter(
    (g) => g.values.length === 1 && g.values[0]!.key == null
  );
  if (keyedGroups.length < 2 && singletonGroups.length === 0) {
    return null;
  }

  // Last preamble heading before groups = section title; earlier content = intro.
  // Drop a trailing short dimension label (e.g. "Position") from the section title.
  let sectionTitle: string | null = null;
  const introParts: string[] = [];
  const headingIdxs = preamble
    .map((p, i) => (p.kind === "heading" ? i : -1))
    .filter((i) => i >= 0);
  if (headingIdxs.length) {
    let sectionIdx = headingIdxs[headingIdxs.length - 1]!;
    const lastHeading = preamble[sectionIdx]!.text;
    if (lastHeading.split(/\s+/).length <= 2 && headingIdxs.length >= 2) {
      sectionIdx = headingIdxs[headingIdxs.length - 2]!;
    }
    sectionTitle = preamble[sectionIdx]!.text;
    for (let i = 0; i < preamble.length; i++) {
      if (i === sectionIdx) continue;
      // Skip the short dimension label immediately after the section title.
      if (
        i > sectionIdx &&
        preamble[i]!.kind === "heading" &&
        preamble[i]!.text.split(/\s+/).length <= 2
      ) {
        continue;
      }
      introParts.push(
        preamble[i]!.kind === "heading"
          ? `**${preamble[i]!.text}**`
          : preamble[i]!.text
      );
    }
  } else {
    for (const p of preamble) introParts.push(p.text);
  }

  const pivoted = pivotGroupsToTable(groups, rowHeaderLabel);
  const remaining = groups.filter((_, i) => !pivoted?.used.has(i));
  const { callouts, leftover } = groupsToCallouts(remaining);

  if (!pivoted?.table && callouts.length === 0 && leftover.length === 0) {
    return null;
  }
  if (
    !(pivoted?.table && pivoted.table.rows.length >= 2) &&
    callouts.length === 0
  ) {
    return null;
  }

  const sheetBase = {
    table: pivoted?.table ?? null,
    callouts,
    leftoverGroups: leftover,
  };
  const displayed = collectDisplayedValues(sheetBase);
  const trailingJoined = trailingLines
    .map((l) => (isHeading(l) ? headingText(l) : cleanLine(l)))
    .filter(Boolean)
    .join("\n\n")
    .trim();
  const trailingProse =
    trailingJoined && !isDuplicateSpecSummary(trailingJoined, displayed)
      ? trailingJoined
      : null;

  return {
    intro: introParts.join("\n\n").trim() || null,
    sectionTitle,
    table: sheetBase.table,
    callouts: sheetBase.callouts,
    leftoverGroups: sheetBase.leftoverGroups,
    notesTitle: notes.length ? notesTitle || "Notes" : null,
    notes,
    trailingProse,
  };
}

/** Whether the answer should use the compact specification sheet layout. */
export function shouldUseSpecSheetView(text: string): boolean {
  return parseGroupedSpecificationAnswer(text) != null;
}
